import { SubmissionResponse, SubmissionRequestBody, BountyDetail } from '../types';
import { BASE_URL, createHeaders } from '../utils';

// --- Submissions --- //

/**
 * Add a new submission to the Submissions table
 */
export async function addSubmission(requestBody: SubmissionRequestBody): Promise<SubmissionResponse> {
  const headers = await createHeaders();
  const response = await fetch(`${BASE_URL}/submissions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})); // Try to parse error, default to empty object
    console.error('Add Submission Error:', response.status, errorData);
    // Check if a detailed error message exists in the response body
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      // Check for the specific 'before' condition
      if (detailMessage.toLowerCase().includes('before')) {
         throw new Error('This clip was posted before the bounty began.');
      }
      throw new Error(detailMessage); // Throw original detail message otherwise
    }
    // Fallback to generic error message
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * List all submissions for a user
 */
export async function listUserSubmissions(bountyId?: string): Promise<SubmissionResponse[]> {
  const headers = await createHeaders();
  
  // Build URL with optional bounty_id query parameter
  let url = `${BASE_URL}/submissions`;
  if (bountyId) {
    url += `?bounty_id=${encodeURIComponent(bountyId)}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('List User Submissions Error:', response.status, errorData);
    // Check for detail message here as well
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      throw new Error(detailMessage);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * Get sum of user's total payments
 * @param bountyId - Optional bounty ID to filter payments by
 * @returns The total payment amount as a number
 */
export async function getTotalPayouts(bountyId?: string): Promise<number> {
  const headers = await createHeaders();
  
  // Build URL with required bounty_id query parameter
  let url = `${BASE_URL}/submissions/payments-total`;
  if (bountyId) {
    url += `?bounty_id=${encodeURIComponent(bountyId)}`;
  } else {
    // If no bountyId is provided, we still need to include it as per the API spec
    url += `?bounty_id=`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('Get Total Payments Error:', response.status, errorData);
    // Check for detail message
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      throw new Error(detailMessage);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  // Parse the JSON response and return the total_payments field
  const data = await response.json();
  return data.total_payments;
}

// --- Bounties --- //

/**
 * Get all available bounties with their details
 */
export async function getBounties(): Promise<BountyDetail[]> {
  // No authentication needed for public bounties
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');

  const response = await fetch(`${BASE_URL}/bounties`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('Get Bounties Error:', response.status, errorData);
    // Check for detail message
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      throw new Error(detailMessage);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * Delete a specific submission belonging to the authenticated user
 * @param submissionId - The ID of the submission to delete
 * @returns A response object indicating success
 */
export async function deleteSubmission(submissionId: string): Promise<any> {
  const headers = await createHeaders();
  const response = await fetch(`${BASE_URL}/submissions/${submissionId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('Delete Submission Error:', response.status, errorData);
    // Check for detail message
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      throw new Error(detailMessage);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * Get a specific bounty by its ID
 * @param bountyId - The ID of the bounty to retrieve
 * @returns The bounty details
 */
export async function getBountyById(bountyId: string): Promise<BountyDetail> {
  const headers = await createHeaders();
  const response = await fetch(`${BASE_URL}/bounties/${bountyId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('Get Bounty Error:', response.status, errorData);
    // Check for detail message
    const detailMessage = errorData?.detail;
    if (typeof detailMessage === 'string' && detailMessage.trim() !== '') {
      throw new Error(detailMessage);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

